import React, { useState, useEffect } from 'react';
import { User, Phone, ChevronDown, Stethoscope } from 'lucide-react';
import axios from 'axios';
import { getApiUrl } from "../../config/api";

export default function VeterinarianWidget({ config = {}, onConfigChange }) {
  const [veterinarians, setVeterinarians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const {
    title = 'Primary Veterinarian',
    showClinic = true,
    showEmail = false,
    selectedVetId = null
  } = config;

  // Find the selected veterinarian from the config
  const selectedVet = veterinarians.find(vet => vet.id === selectedVetId);

  useEffect(() => {
    fetchVeterinarians();
  }, []);

  const fetchVeterinarians = async () => {
    try {
      setLoading(true);
      const response = await axios.get(getApiUrl('/api/veterinarians'));

      if (response.data) {
        setVeterinarians(response.data);
        
        // Set the first veterinarian as selected by default if none is selected
        if (response.data.length > 0 && !selectedVetId && onConfigChange) {
          onConfigChange({
            ...config,
            selectedVetId: response.data[0].id
          });
        }
      }
    } catch (error) {
      console.error('Error fetching veterinarians:', error);
      setError('Failed to load veterinarians');
    } finally {
      setLoading(false);
    }
  };



  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex items-center gap-2 mb-3">
          <Stethoscope className="h-5 w-5 text-purple-600" />
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        </div>
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-gray-200 rounded"></div>
          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex items-center gap-2 mb-3">
          <Stethoscope className="h-5 w-5 text-purple-600" />
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        </div>
        <p className="text-red-600 text-sm">{error}</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      <div className="flex items-center gap-2 mb-3">
        <div className="flex items-center gap-2">
          <Stethoscope className="h-5 w-5 text-purple-600" />
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        </div>
      </div>

      {veterinarians.length === 0 ? (
        <div className="text-center py-4">
          <p className="text-gray-500 text-sm mb-3">No veterinarians found</p>
          <a 
            href="/veterinarians" 
            className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 text-sm"
          >
            <User className="h-4 w-4" />
            Add Veterinarian
          </a>
        </div>
             ) : (
         <div className="space-y-4">
           {/* Selected Veterinarian Display */}
           {selectedVet ? (
             <div className="bg-gray-50 rounded-lg p-4 space-y-3">
               <div className="flex items-center gap-2">
                 <User className="h-4 w-4 text-purple-600" />
                 <span className="font-medium text-gray-900">{selectedVet.name}</span>
               </div>
               
               {selectedVet.phone && (
                 <div className="flex items-center gap-2">
                   <Phone className="h-4 w-4 text-gray-500" />
                   <span className="text-sm text-gray-700">{selectedVet.phone}</span>
                 </div>
               )}

               {showClinic && selectedVet.clinic_name && (
                 <div className="text-sm text-gray-600">
                   <span className="font-medium">Clinic:</span> {selectedVet.clinic_name}
                 </div>
               )}

               {showEmail && selectedVet.email && (
                 <div className="text-sm text-gray-600">
                   <span className="font-medium">Email:</span> {selectedVet.email}
                 </div>
               )}

               {selectedVet.specialties && (
                 <div className="text-sm text-gray-600">
                   <span className="font-medium">Specialties:</span> {selectedVet.specialties}
                 </div>
               )}
             </div>
           ) : (
             <div className="text-center py-4">
               <p className="text-gray-500 text-sm">No veterinarian selected</p>
               <p className="text-gray-400 text-xs mt-1">Configure widget to select a veterinarian</p>
             </div>
           )}
         </div>
       )}
    </div>
  );
} 